// physics.js — Physik für Gesten und unterbrechbare Federn (Bewegung Runde 3, Plan docs/superpowers/plans/2026-10-22-bewegung-runde-3.md).
// Ergänzt motion.js: play() (Web Animations) startet jede Feder ohne Schwung; hier laufen Federn auf einem gemeinsamen
// rAF-Takt und übernehmen die Geschwindigkeit — ein Wurf geht in die Feder über, ein neues Ziel mitten im Federn
// setzt beim aktuellen Stand UND der aktuellen Geschwindigkeit an. Rein bis auf ticker/drag/haptic (DOM, injizierbar).
import { SPRINGS, springStep } from './spring-math.js';

/** Längster Zeitschritt (s): ein angehaltener Tab oder ein langer Rahmen lässt die Feder nicht springen. */
export const MAX_DT = 0.05;

const isReduced = () => typeof document !== 'undefined' && (document.documentElement.dataset.motion === 'reduced'
  || (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches));

// ── Takt ──────────────────────────────────────────────────────────────────────────────────────────────────────
/**
 * Ein gemeinsamer Takt für alle laufenden Federn: höchstens EINE rAF-Schleife, sie endet, sobald nichts mehr läuft.
 * add(fn): fn(dt) wird je Bild gerufen, gibt false zurück, wenn sie fertig ist. Gibt eine Abmeldefunktion zurück.
 */
export function createTicker({ raf, caf, now } = {}) {
  const R = raf || ((f) => requestAnimationFrame(f));
  const C = caf || ((id) => cancelAnimationFrame(id));
  const N = now || (() => performance.now());
  const jobs = new Set();
  let id = 0, last = null;
  // Zeitstempel des Bildes (rAF-Argument), nicht die Uhr beim Aufruf: der Aufruf kommt je nach Last unterschiedlich spät
  // im Bild, mit der Uhr wären die Schritte ungleich groß (Feder ruckelt, obwohl jedes Bild kommt). Erstes Bild: 1/60 s.
  const frame = (ts) => {
    const t = typeof ts === 'number' ? ts : N();
    const dt = last == null ? 1 / 60 : Math.min(MAX_DT, Math.max(0, (t - last) / 1000));
    last = t;
    for (const j of [...jobs]) {
      let keep = false;
      try { keep = j(dt) !== false; } catch (e) { keep = false; setTimeout(() => { throw e; }); }
      if (!keep) jobs.delete(j);
    }
    id = jobs.size ? R(frame) : 0;
  };
  return {
    add(fn) {
      jobs.add(fn);
      if (!id) { last = null; id = R(frame); }
      return () => { jobs.delete(fn); if (!jobs.size && id) { C(id); id = 0; } };
    },
    get running() { return id !== 0; },
    get size() { return jobs.size; },
  };
}

let shared = null;
/** Der Takt der App (lazy, damit Node-Tests ohne rAF importieren können). */
export const ticker = () => (shared ||= createTicker());

// ── Feder ─────────────────────────────────────────────────────────────────────────────────────────────────────
const keysOf = (v) => (typeof v === 'number' ? null : Object.keys(v));
const map = (v, f) => (typeof v === 'number' ? f(v, null) : Object.fromEntries(Object.keys(v).map((k) => [k, f(v[k], k)])));

/**
 * Unterbrechbare Feder auf einer Zahl oder einem Objekt aus Zahlen ({ x, y, scale … }).
 *   const s = spring({ x: 0 }, { spring: 'spatial-fast', onUpdate: ({ x }) => el.style.translate = x + 'px 0' })
 *   s.to({ x: 120 }, { velocity: { x: 1800 } })  // px/s
 * o: { spring = 'spatial-default', precision = 0.1, onUpdate, onRest, reduced, ticker }
 * to() gibt ein Promise, das beim Ruhen (oder Überholen durch ein neues Ziel) erfüllt wird.
 */
export function spring(initial, o = {}) {
  const model = (name) => { const m = SPRINGS[name || 'spatial-default']; if (!m) throw new Error('unbekannte Feder: ' + name); return m; };
  let S = model(o.spring);
  const keys = keysOf(initial);
  const eps = o.precision ?? 0.1;
  let x = map(initial, (n) => n), v = map(initial, () => 0), target = map(initial, (n) => n);
  let off = null, waiters = [];
  const emit = () => { if (o.onUpdate) o.onUpdate(x, v); };
  const settle = () => { const w = waiters; waiters = []; w.forEach((r) => r()); if (o.onRest) o.onRest(x); };
  const atRest = () => (keys || [null]).every((k) => {
    const xi = k == null ? x : x[k], vi = k == null ? v : v[k], ti = k == null ? target : target[k];
    return Math.abs(xi - ti) < eps && Math.abs(vi) < eps * 10;
  });
  const step = (dt) => {
    if (keys) {
      const nx = {}, nv = {};
      for (const k of keys) { const s = springStep({ x: x[k], v: v[k] }, target[k], S.zeta, S.stiffness, dt); nx[k] = s.x; nv[k] = s.v; }
      x = nx; v = nv;
    } else { const s = springStep({ x, v }, target, S.zeta, S.stiffness, dt); x = s.x; v = s.v; }
    if (atRest()) { x = map(target, (n) => n); v = map(target, () => 0); emit(); off = null; settle(); return false; }
    emit();
    return true;
  };
  const api = {
    get value() { return x; },
    get velocity() { return v; },
    get target() { return target; },
    get running() { return off !== null; },
    /** Neues Ziel; velocity (Einheiten/s) ersetzt die aktuelle Geschwindigkeit, sonst wird sie übernommen; spring wechselt das Federmodell. */
    to(t, { velocity, spring: name } = {}) {
      if (name) S = model(name);
      target = keys ? { ...target, ...t } : t;
      if (velocity != null) v = keys ? { ...v, ...velocity } : velocity;
      if (o.reduced ?? isReduced()) { api.jump(target); return Promise.resolve(); }
      const p = new Promise((r) => waiters.push(r));
      if (!off) off = (o.ticker || ticker()).add(step);
      return p;
    },
    /** Sofort auf einen Wert, ohne Bewegung (auch beim Ziehen: der Finger bestimmt den Wert). */
    jump(t) {
      if (off) { off(); off = null; }
      x = keys ? { ...x, ...t } : t; target = keys ? { ...target, ...t } : t; v = map(x, () => 0);
      emit(); settle();
    },
    stop() { if (off) { off(); off = null; } v = map(x, () => 0); target = map(x, (n) => n); settle(); },
  };
  return api;
}

// ── Geschwindigkeit aus Zeigerpunkten ─────────────────────────────────────────────────────────────────────────
/**
 * Geschwindigkeit (Einheiten/s) aus den Punkten der letzten 100 ms, Kleinste-Quadrate-Gerade.
 * Eine Pause über 40 ms gilt als Stillstand (Android: ASSUME_POINTER_STOPPED_TIME) — ein Finger, der angehalten und
 * dann losgelassen hat, wirft nicht.
 */
export function velocityTracker({ horizon = 100, stopped = 40 } = {}) {
  let pts = [];
  return {
    add(t, value) {
      if (pts.length && t - pts[pts.length - 1].t > stopped) pts = [];
      pts.push({ t, value });
      while (pts.length && t - pts[0].t > horizon) pts.shift();
    },
    velocity(now) {
      if (now != null && pts.length && now - pts[pts.length - 1].t > stopped) return 0;
      if (pts.length < 2) return 0;
      const n = pts.length, t0 = pts[n - 1].t;
      let st = 0, sv = 0, stt = 0, stv = 0;
      for (const p of pts) { const ti = (p.t - t0) / 1000; st += ti; sv += p.value; stt += ti * ti; stv += ti * p.value; }
      const den = n * stt - st * st;
      return den ? (n * stv - st * sv) / den : 0;
    },
    reset() { pts = []; },
  };
}

/**
 * Zeigerpunkte eines move-Ereignisses in den Tracker. Hängt der Hauptthread ein Bild lang, fasst der Browser alle
 * Bewegungen dazwischen zu EINEM Ereignis zusammen; ohne die Einzelpunkte (getCoalescedEvents, mit ihren echten
 * Zeitstempeln) sähe der Tracker eine Lücke über 40 ms und hielte einen Wurf für Stillstand — auf langsamen Handys
 * genau dann, wenn die Seite ruckelt. map(Ereignis) → Wert.
 */
export function trackMove(tr, e, map) {
  let evs = null;
  try { evs = e.getCoalescedEvents ? e.getCoalescedEvents() : null; } catch { evs = null; }
  if (!evs || !evs.length) evs = [e];
  for (const c of evs) tr.add(c.timeStamp, map(c));
}

// ── Auslaufen (Schwung) ───────────────────────────────────────────────────────────────────────────────────────
/** Reibung wie Compose exponentialDecay(): x(t) = x0 + v0/k·(1 − e^(−k·t)), k = 4,2 · friction. */
export const DECAY_K = 4.2;
export function decayAt(x0, v0, t, friction = 1) {
  const k = DECAY_K * friction;
  return { x: x0 + (v0 / k) * (1 - Math.exp(-k * t)), v: v0 * Math.exp(-k * t) };
}
/** Wohin ein Wurf ausliefe — Grundlage für die Wahl des Rastpunkts. */
export const projectedEnd = (x0, v0, friction = 1) => x0 + v0 / (DECAY_K * friction);

/** Nächster Rastpunkt zur projizierten Endlage (Index). */
export function snapIndex(x0, v0, snaps, friction = 1) {
  const p = projectedEnd(x0, v0, friction);
  let best = 0;
  for (let i = 1; i < snaps.length; i++) if (Math.abs(snaps[i] - p) < Math.abs(snaps[best] - p)) best = i;
  return best;
}

// ── Gummiband ─────────────────────────────────────────────────────────────────────────────────────────────────
/** Widerstand jenseits einer Grenze (iOS-Formel): wächst monoton, bleibt unter dim. over darf negativ sein. */
export function rubberBand(over, dim, c = 0.55) {
  if (!dim || !over) return 0;
  const a = Math.abs(over);
  return Math.sign(over) * (1 - 1 / ((a * c) / dim + 1)) * dim;
}
/** Wert in [min, max] klemmen, darüber hinaus mit Gummiband. */
export function rubberClamp(value, min, max, dim, c = 0.55) {
  if (value < min) return min + rubberBand(value - min, dim, c);
  if (value > max) return max + rubberBand(value - max, dim, c);
  return value;
}

// ── Ziehen ────────────────────────────────────────────────────────────────────────────────────────────────────
/**
 * Geste auf einer Achse. Das Element folgt dem Finger (onMove), losgelassen meldet sie Wert und Wurf (onRelease) —
 * was dann federt, entscheidet der Aufrufer (meist spring().to(snap, { velocity })).
 *   drag(el, { axis: 'x', value: () => s.value, bounds: () => [min, max], onStart, onMove, onRelease, filter })
 * Zuhörer für move/up hängen am Fenster (ein Umsortieren im DOM würde die Zeigererfassung lösen). Bewegt sich der
 * Finger zuerst quer zur Achse, gibt die Geste ab — die Seite scrollt weiter.
 */
export function drag(el, o) {
  const axis = o.axis || 'y';
  const coord = (e) => (axis === 'x' ? e.clientX : e.clientY);
  const cross = (e) => (axis === 'x' ? e.clientY : e.clientX);
  const slop = o.slop ?? 6;
  let g = null;
  const down = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    if (o.filter && !o.filter(e)) return;
    g = { id: e.pointerId, p0: coord(e), c0: cross(e), v0: o.value ? o.value() : 0, active: false, tr: velocityTracker() };
    g.tr.add(e.timeStamp, coord(e));
  };
  const move = (e) => {
    if (!g || e.pointerId !== g.id) return;
    const d = coord(e) - g.p0, dc = cross(e) - g.c0;
    if (!g.active) {
      if (Math.abs(d) < slop && Math.abs(dc) < slop) return;
      if (Math.abs(dc) > Math.abs(d)) { g = null; return; }
      g.active = true;
      try { el.setPointerCapture?.(e.pointerId); } catch { /* Element nicht mehr im DOM */ }
      if (o.onStart) o.onStart(e);
    }
    e.preventDefault();
    trackMove(g.tr, e, coord);
    let value = g.v0 + d;
    const b = o.bounds && o.bounds();
    if (b) value = rubberClamp(value, b[0], b[1], o.dim ? o.dim() : Math.max(1, b[1] - b[0]) || 1);
    g.value = value;
    if (o.onMove) o.onMove(value, e);
  };
  const up = (e) => {
    if (!g || (e && e.pointerId !== g.id)) return;
    const s = g; g = null;
    if (!s.active) { if (o.onTap) o.onTap(e); return; }
    const velocity = e && e.type === 'pointercancel' ? 0 : s.tr.velocity(e ? e.timeStamp : undefined);
    if (o.onRelease) o.onRelease({ value: s.value ?? s.v0, velocity, delta: (s.value ?? s.v0) - s.v0 }, e);
  };
  el.addEventListener('pointerdown', down);
  addEventListener('pointermove', move, { passive: false });
  addEventListener('pointerup', up);
  addEventListener('pointercancel', up);
  return () => {
    el.removeEventListener('pointerdown', down);
    removeEventListener('pointermove', move); removeEventListener('pointerup', up); removeEventListener('pointercancel', up);
  };
}

// ── Haptik ────────────────────────────────────────────────────────────────────────────────────────────────────
export const HAPTICS = { tick: 10, snap: 15, confirm: 25 };
/**
 * Fühlbare Rückmeldung bei Rastpunkt, Umschalten, Ablegen. In der ViaCamp-App über die Brücke (Handler 'haptic',
 * ältere Apps ignorieren ihn), sonst navigator.vibrate (Android-Chrome; iOS-Safari kennt es nicht). Nie bei
 * reduzierter Bewegung. Gibt zurück, ob etwas ausgelöst wurde.
 */
export function haptic(kind = 'tick', win = typeof window !== 'undefined' ? window : null, reduced = isReduced()) {
  const ms = HAPTICS[kind];
  if (!ms || !win || reduced) return false;
  const b = win.flutter_inappwebview;
  if (b && typeof b.callHandler === 'function') {
    try { Promise.resolve(b.callHandler('haptic', { kind })).catch(() => {}); return true; } catch { return false; }
  }
  const nav = win.navigator;
  if (nav && typeof nav.vibrate === 'function') { try { return nav.vibrate(ms) !== false; } catch { return false; } }
  return false;
}
